import { Box } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { KeyboardEvent, ReactNode, Ref, useRef } from "react";

type Props<TItem> = {
  items: TItem[];
  getItemKey: (item: TItem, index: number) => string;
  renderItem: (item: TItem, index: number) => ReactNode;
  // Starting height guess per row. Rows are measured for real once mounted, so this only affects the
  // scrollbar length before the first measurement — a rough value is enough.
  estimateSize: number;
  // Vertical space between rows, applied as padding inside each measured row so the virtualizer's
  // own measurement already accounts for it (a CSS `gap` on an absolutely-positioned child would not).
  gap: number;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  scrollRef?: Ref<HTMLDivElement>;
};

// Windowed list: only the rows intersecting the viewport (plus a small overscan) are mounted.
// Rows here are MUI Accordions whose height changes when they expand, so heights are measured at
// runtime via measureElement rather than assumed fixed.
export function VirtualList<TItem>(props: Props<TItem>) {
  const { items, getItemKey, renderItem, estimateSize, gap, onKeyDown, scrollRef } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => estimateSize,
    getItemKey: index => getItemKey(items[index], index),
    overscan: 8
  });

  return (
    <Box
      ref={(node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof scrollRef === "function") scrollRef(node);
        else if (scrollRef) (scrollRef as { current: HTMLDivElement | null }).current = node;
      }}
      sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
      onKeyDown={onKeyDown}
    >
      <Box sx={{ position: "relative", width: "100%" }} style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map(virtualItem => (
          <Box
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            sx={{ position: "absolute", top: 0, left: 0, width: "100%" }}
            // Inline style, not sx: the offset is unique per row, and routing it through Emotion
            // would mint a new generated class for every row on every scroll frame.
            style={{ transform: `translateY(${virtualItem.start}px)`, paddingBottom: gap }}
          >
            {renderItem(items[virtualItem.index], virtualItem.index)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
